import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountWebLayout, AccountPageHeader, AccountStatStrip } from './AccountWebLayout';
import { fetchVipProfile } from '../../utils/vipApi';

export function Vip() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isGuest =
        localStorage.getItem('guestUser') === 'true' || !localStorage.getItem('token');

    const spend = profile?.annualSpend ?? 0;
    const activeTier = profile?.activeTier;
    const nextTier = profile?.nextTier ?? null;
    const maxThreshold = profile?.maxThreshold ?? 0;
    const progressPct = profile?.progressPct ?? 0;
    const remaining = profile?.remaining ?? 0;
    const tierSteps = profile?.tierSteps ?? [];
    const benefits = profile?.benefits ?? [];
    const upcomingPerks = profile?.upcomingPerks ?? [];
    const atMaxTier = Boolean(profile && !nextTier);
    const youPinPct = Math.min(92, Math.max(8, progressPct));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (isGuest) {
                if (!cancelled) {
                    setProfile(null);
                    setLoading(false);
                }
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const data = await fetchVipProfile();
                if (!cancelled) setProfile(data);
            } catch (_e) {
                if (!cancelled) {
                    setError('Could not load VIP status. Please try again.');
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isGuest]);

    return (
        <AccountWebLayout title="VIP">
            <AccountPageHeader
                title="VIP membership"
                description="Perks and tier progress based on your delivered order spend this calendar year."
                action={
                    !isGuest ? (
                        <button type="button" className="btn-primary--sm" onClick={() => navigate('/')}>
                            Shop now
                        </button>
                    ) : null
                }
            />

            {isGuest && (
                <div className="account-web-notice">
                    <p>Sign in to see your tier and spend from your orders.</p>
                    <button
                        type="button"
                        className="btn-primary--sm"
                        onClick={() => navigate('/auth/login')}
                    >
                        Sign in
                    </button>
                </div>
            )}

            {error && (
                <div className="account-web-info-block account-web-info-block--error" role="alert">
                    {error}
                </div>
            )}

            {!isGuest && (
                <>
                    <AccountStatStrip
                        items={[
                            {
                                label: 'Current tier',
                                value: loading ? '…' : activeTier ? activeTier.name : '—',
                                tone: 'primary',
                            },
                            {
                                label: 'Spend this year',
                                value: loading ? '…' : `₹${spend.toLocaleString('en-IN')}`,
                            },
                            {
                                label: nextTier ? `To ${nextTier.name}` : 'Status',
                                value: loading
                                    ? '…'
                                    : nextTier
                                      ? `₹${remaining.toLocaleString('en-IN')} left`
                                      : 'Max tier',
                            },
                        ]}
                    />

                    {profile && maxThreshold > 0 && (
                        <section className="account-web-panel">
                            <h2 className="account-web-panel__heading">
                                <i className="bi bi-graph-up-arrow" aria-hidden />
                                Tier progress
                            </h2>
                            <div className="account-web-vip-progress">
                                <div className="account-web-vip-progress__rail-wrap">
                                    <div
                                        className="account-web-vip-progress__pin"
                                        style={{ left: loading ? '8%' : `${youPinPct}%` }}
                                    >
                                        You · ₹{spend.toLocaleString('en-IN')}
                                    </div>
                                    <div className="account-web-vip-progress__rail">
                                        <div
                                            className="account-web-vip-progress__fill"
                                            style={{ width: loading ? '0%' : `${progressPct}%` }}
                                            role="progressbar"
                                            aria-valuenow={Math.round(progressPct)}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                        />
                                    </div>
                                    <div className="account-web-vip-progress__scale">
                                        <span>₹0</span>
                                        <span>
                                            {atMaxTier
                                                ? 'Platinum unlocked'
                                                : `₹${maxThreshold.toLocaleString('en-IN')}`}
                                        </span>
                                    </div>
                                </div>
                                <div className="account-web-vip-progress__tiers">
                                    {tierSteps.map((step) => (
                                        <div
                                            key={step.id}
                                            className={`account-web-vip-tier account-web-vip-tier--${step.status} account-web-vip-tier--${step.name.toLowerCase()}`}
                                            style={{ '--tier-accent': step.accent }}
                                        >
                                            <div className="account-web-vip-tier__dot">
                                                <i className={`bi ${step.icon}`} aria-hidden />
                                            </div>
                                            <span className="account-web-vip-tier__name">{step.name}</span>
                                            <span className="account-web-vip-tier__threshold">
                                                ₹{step.threshold.toLocaleString('en-IN')}+
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <p className="account-web-vip-progress__hint">
                                    {nextTier ? (
                                        <>
                                            <strong>₹{remaining.toLocaleString('en-IN')}</strong> more to unlock{' '}
                                            <strong>{nextTier.name}</strong>
                                        </>
                                    ) : (
                                        <>You have reached Platinum — all perks are active.</>
                                    )}
                                </p>
                            </div>
                        </section>
                    )}

                    {activeTier && benefits.length > 0 && (
                        <section className="account-web-panel">
                            <h2 className="account-web-panel__heading">
                                <i className="bi bi-stars" aria-hidden />
                                Your {activeTier.name} benefits
                            </h2>
                            {benefits.map((b) => (
                                <div key={b.title} className="account-web-benefit">
                                    <i className={`bi ${b.icon}`} aria-hidden />
                                    <div>
                                        <strong>{b.title}</strong>
                                        <p>{b.description}</p>
                                    </div>
                                </div>
                            ))}
                        </section>
                    )}

                    {nextTier && upcomingPerks.length > 0 && (
                        <section className="account-web-panel">
                            <h2 className="account-web-panel__heading">
                                <i className="bi bi-lock" aria-hidden />
                                Unlock {nextTier.name}
                            </h2>
                            <p className="account-web-meta" style={{ marginTop: 0 }}>
                                At ₹{nextTier.threshold.toLocaleString('en-IN')}+ yearly spend
                            </p>
                            {upcomingPerks.map((perk) => (
                                <div
                                    key={perk.title}
                                    className="account-web-benefit account-web-benefit--locked"
                                >
                                    <i className={`bi ${perk.icon}`} aria-hidden />
                                    <div>
                                        <strong>{perk.title}</strong>
                                        <p>{perk.description}</p>
                                    </div>
                                </div>
                            ))}
                        </section>
                    )}
                </>
            )}
        </AccountWebLayout>
    );
}
